import { generateId, getPhaseColor } from '../utils/helpers';
import { Initiative, Feature } from '../types/index';
import { WORK_PRODUCT_LIBRARY } from '../constants/index';
import { deleteProjectApi } from '../../../api/api';

export const createProjectHandlers = (
  setInitiatives: React.Dispatch<React.SetStateAction<Initiative[]>>,
  setSelectedProjectId: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedFeature: React.Dispatch<React.SetStateAction<Feature | null>>,
  setLastUsedMatrixId: React.Dispatch<React.SetStateAction<string | null>>,
  handleViewChange: (view: string) => void,
  initiatives: Initiative[],
  token?: string | null
) => {
  const handleCreateProject = (projectName: string) => {
    const newProjectId = generateId();
    
    // Generate 3-5 sample features
    const sampleFeatures: Feature[] = [];
    const featureCount = Math.floor(Math.random() * 3) + 3;
    
    const featureTemplates = [
      'User Authentication', 'Dashboard Interface', 'Data Management', 'Reporting System',
      'Mobile Integration', 'API Gateway', 'Search Functionality', 'Notification System',
      'Security Framework', 'Analytics Engine'
    ];
    
    const selectedTemplates = featureTemplates
      .sort(() => 0.5 - Math.random())
      .slice(0, featureCount);
    
    selectedTemplates.forEach((template) => {
      const featureId = generateId();
      
      sampleFeatures.push({
        id: featureId,
        name: template,
        sdlcPhases: Object.keys(WORK_PRODUCT_LIBRARY).map(phaseId => ({
          id: phaseId,
          name: phaseId === 'qa' ? 'QA' : phaseId.charAt(0).toUpperCase() + phaseId.slice(1),
          color: getPhaseColor(phaseId),
          workProducts: []
        })),
        detailedRequirements: [
          {
            id: generateId(),
            name: `${template} Requirements`,
            description: `Core requirements for ${template} implementation`,
            status: 'active',
            approved: Math.random() > 0.5
          },
          {
            id: generateId(),
            name: `${template} Specifications`,
            description: `Technical specifications for ${template}`,
            status: 'available',
            approved: false
          }
        ],
        uploads: []
      });
    });

    // Generate project code
    const projectWords = projectName.split(' ').filter(word => word.length > 2);
    const projectCode = projectWords
      .slice(0, 3)
      .map(word => word.charAt(0).toUpperCase())
      .join('') + '-' + new Date().getFullYear();

    const newProject: Initiative = {
      id: newProjectId,
      name: projectName,
      code: projectCode,
      description: `${projectName} - A comprehensive project featuring modern architecture, user-centric design, and scalable functionality.`,
      dueDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { 
        month: 'long', day: 'numeric', year: 'numeric' 
      }),
      startDate: new Date().toLocaleDateString('en-US', { 
        month: 'long', day: 'numeric', year: 'numeric' 
      }),
      teamSize: Math.floor(Math.random() * 6) + 4,
      progress: Math.floor(Math.random() * 15) + 5,
      status: 'active',
      theme: ['Innovation', 'Digital Transformation', 'Customer Experience', 'Operational Excellence'][Math.floor(Math.random() * 4)],
      owner: 'Project Manager',
      createdDate: new Date().toLocaleDateString('en-US', { 
        month: 'long', day: 'numeric', year: 'numeric' 
      }),
      riskLevel: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high',
      blockers: Math.floor(Math.random() * 3),
      lastUpdated: 'Just now',
      lastEditor: 'System',
      notifications: Math.floor(Math.random() * 5),
      teamMembers: [
        { id: generateId(), name: 'Alex Johnson', avatar: '👨‍💼', role: 'Project Manager' },
        { id: generateId(), name: 'Sarah Chen', avatar: '👩‍💻', role: 'Lead Developer' },
        { id: generateId(), name: 'Mike Davis', avatar: '👨‍🎨', role: 'UI/UX Designer' },
        { id: generateId(), name: 'Emma Wilson', avatar: '👩‍🔧', role: 'QA Engineer' }
      ],
      milestones: [
        { 
          id: generateId(), 
          name: 'Project Kickoff', 
          date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), 
          completed: true 
        },
        { 
          id: generateId(), 
          name: 'Requirements Complete', 
          date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), 
          completed: false 
        },
        { 
          id: generateId(), 
          name: 'MVP Release', 
          date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), 
          completed: false 
        }
      ],
      features: sampleFeatures,
      uploads: []
    };

    setInitiatives(prev => [...prev, newProject]);
    setSelectedProjectId(newProjectId);
  };

  const handleSelectProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    
    const selectedProject = initiatives.find(init => init.id === projectId);
    if (selectedProject && selectedProject.features.length > 0) {
      const firstFeature = selectedProject.features[0];
      setSelectedFeature(firstFeature);
      setLastUsedMatrixId(firstFeature.id);
      
      // Navigate to Feature Studio immediately after project selection
      handleViewChange('features');
    } else {
      // If no features exist, still navigate but clear feature selection
      setSelectedFeature(null);
      setLastUsedMatrixId(null);
      handleViewChange('features');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      // Call the API to delete from database if token is available
      if (token) {
        await deleteProjectApi(parseInt(projectId), token);
      }

      // Update local state after successful deletion
      setInitiatives(prev => prev.filter(init => init.id !== projectId));

      if (setSelectedProjectId) {
        setSelectedProjectId(null);
        setSelectedFeature(null);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      // Re-throw the error so the UI can handle it
      throw error;
    }
  };

  const handleBackToProjects = () => {
    handleViewChange('projects');
    setSelectedFeature(null);
    setSelectedProjectId(null);
  };

  return {
    handleCreateProject,
    handleSelectProject,
    handleDeleteProject,
    handleBackToProjects
  };
};